/** Respects `homepage` / `PUBLIC_URL` when the app is not served from `/`. */
export function publicBlogAssetPath(path: string): string {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}

export const BLOG_MANIFEST_URL = publicBlogAssetPath('/blog_posts/manifest.json');

export type BlogManifest = {
  posts: string[];
};

export type ParsedPost = {
  slug: string;
  frontmatter: Record<string, string>;
  body: string;
};

export function markdownUrlForSlug(slug: string): string {
  return publicBlogAssetPath(
    `/blog_posts/${encodeURIComponent(slug)}/${encodeURIComponent(slug)}.md`
  );
}

/**
 * Minimal YAML frontmatter (key: value per line). Values can be quoted.
 * Omit frontmatter by starting the file with regular markdown.
 */
export function parseMarkdownWithFrontmatter(raw: string): {
  frontmatter: Record<string, string>;
  body: string;
} {
  const trimmed = raw.trimStart();
  if (!trimmed.startsWith('---')) {
    return { frontmatter: {}, body: raw.trimEnd() };
  }
  const afterOpen = trimmed.slice(3).replace(/^\r?\n/, '');
  const closeIdx = afterOpen.search(/\r?\n---(\r?\n|$)/);
  if (closeIdx === -1) {
    return { frontmatter: {}, body: raw.trimEnd() };
  }
  const fmBlock = afterOpen.slice(0, closeIdx).trim();
  const body = afterOpen.slice(closeIdx).replace(/^\r?\n---\r?\n?/, '').trimEnd();
  const frontmatter: Record<string, string> = {};
  for (const line of fmBlock.split(/\r?\n/)) {
    const m = /^([\w-]+):\s*(.*)$/.exec(line);
    if (!m) continue;
    let v = m[2].trim();
    if (
      (v.startsWith('"') && v.endsWith('"')) ||
      (v.startsWith("'") && v.endsWith("'"))
    ) {
      v = v.slice(1, -1);
    }
    frontmatter[m[1]] = v;
  }
  return { frontmatter, body };
}

export async function fetchBlogManifest(): Promise<string[]> {
  const res = await fetch(BLOG_MANIFEST_URL);
  if (!res.ok) throw new Error('Failed to load blog manifest');
  const data = (await res.json()) as BlogManifest;
  if (!Array.isArray(data.posts)) return [];
  return data.posts.filter((s) => typeof s === 'string' && s.length > 0);
}

export async function fetchPostMarkdown(slug: string): Promise<ParsedPost> {
  const res = await fetch(markdownUrlForSlug(slug));
  if (!res.ok) throw new Error('Failed to load post');
  const raw = await res.text();
  const { frontmatter, body } = parseMarkdownWithFrontmatter(raw);
  return { slug, frontmatter, body };
}

export function postTitle(parsed: ParsedPost): string {
  return (
    parsed.frontmatter.title?.trim() ||
    parsed.slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

export function postDescription(parsed: ParsedPost): string {
  return (
    parsed.frontmatter.description?.trim() ||
    'Tips, tutorials, and product updates from Substyle — animated subtitles in Adobe Premiere Pro.'
  );
}

export function postDatePublished(parsed: ParsedPost): string | undefined {
  const d = parsed.frontmatter.datePublished?.trim();
  return d || undefined;
}
